import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { EditorialQuestion, EditorialQuestionInput } from 'api/editorialQuestion.mock';

type DraftField =
  | keyof EditorialQuestionInput
  | `sectionNotes.${string}`
  | `options.${number}.content`
  | `options.${number}.jyutping`
  | `options.${number}.imagePrompt`;

type DraftQuestionState = {
  current: EditorialQuestionInput | null;
  persisted: EditorialQuestion | null;
  pendingAssetBlobs: Record<string, string>;
  /** Bumped on every resetDraft, i.e. each time a question is loaded/switched
   *  into the editor. The Godot preview bridge keys its full LOAD_LEVEL reset
   *  on this — the draft itself carries no stable per-question id (toInput
   *  drops the binding), so without a nonce the bridge can't tell one question
   *  from the next and the previous question's assets bleed into the preview. */
  loadNonce: number;
};

const initialState: DraftQuestionState = {
  current: null,
  persisted: null,
  pendingAssetBlobs: {},
  loadNonce: 0
};

// react-hook-form and this slice are fed the SAME object references from the
// editor (QuestionEditor passes one `nextDraft` to both reset() and
// resetDraft(); setField hands its array to both setValue() and update()).
// Redux Toolkit's Immer deep-freezes state in dev, so storing those shared
// references freezes the very arrays/objects RHF later mutates in place —
// producing "Cannot assign to read only property" on every edit/add/delete.
// Deep-cloning anything that enters the store keeps Redux's copy separate
// (and frozen) while leaving RHF's copy writable. EditorialQuestionInput is
// plain JSON (strings/numbers/arrays), so a structural clone is safe.
function cloneForStore<T>(value: T): T {
  return value == null ? value : (JSON.parse(JSON.stringify(value)) as T);
}

const slice = createSlice({
  name: 'draftQuestion',
  initialState,
  reducers: {
    resetDraft(state, action: PayloadAction<EditorialQuestionInput>) {
      state.current = cloneForStore(action.payload);
      state.persisted = null;
      state.pendingAssetBlobs = {};
      state.loadNonce += 1;
    },
    changeTemplate(
      state,
      action: PayloadAction<
        Pick<EditorialQuestionInput, 'template' | 'options' | 'correctOptionId'>
      >
    ) {
      if (state.current) {
        Object.assign(state.current, cloneForStore(action.payload));
      }
    },
    update(state, action: PayloadAction<{ field: DraftField; value: unknown }>) {
      const { field, value } = action.payload;

      if (!state.current) {
        return;
      }

      if (field.startsWith('sectionNotes.')) {
        const noteField = field.replace(
          'sectionNotes.',
          ''
        ) as keyof EditorialQuestionInput['sectionNotes'];
        state.current.sectionNotes = {
          ...state.current.sectionNotes,
          [noteField]: String(value || '')
        };
        return;
      }

      const optionMatch = field.match(/^options\.(\d+)\.content$/);
      if (optionMatch) {
        const index = Number(optionMatch[1]);
        state.current.options = state.current.options.map((option, optionIndex) =>
          optionIndex === index ? { ...option, content: String(value || '') } : option
        );
        return;
      }

      const optionJyutpingMatch = field.match(/^options\.(\d+)\.jyutping$/);
      if (optionJyutpingMatch) {
        const index = Number(optionJyutpingMatch[1]);
        state.current.options = state.current.options.map((option, optionIndex) =>
          optionIndex === index ? { ...option, jyutping: String(value || '') } : option
        );
        return;
      }

      const optionImagePromptMatch = field.match(/^options\.(\d+)\.imagePrompt$/);
      if (optionImagePromptMatch) {
        const index = Number(optionImagePromptMatch[1]);
        state.current.options = state.current.options.map((option, optionIndex) =>
          optionIndex === index ? { ...option, imagePrompt: String(value || '') } : option
        );
        return;
      }

      state.current = {
        ...state.current,
        // Clone object/array values (e.g. the whole `options` array on
        // add/remove) so Immer freezes Redux's copy, not RHF's live array.
        [field]: typeof value === 'object' && value !== null ? cloneForStore(value) : value
      };
    },
    setAsset(
      state,
      action: PayloadAction<{ field: keyof EditorialQuestionInput; blobUrl: string }>
    ) {
      const { field, blobUrl } = action.payload;

      if (!state.current) {
        return;
      }

      state.current = {
        ...state.current,
        [field]: blobUrl
      };
      state.pendingAssetBlobs[field] = blobUrl;
    },
    replaceAsset(
      state,
      action: PayloadAction<{
        field: keyof EditorialQuestionInput;
        blobUrl: string;
        ossUrl: string;
      }>
    ) {
      const { field, blobUrl, ossUrl } = action.payload;

      if (!state.current) {
        return;
      }

      state.current = {
        ...state.current,
        [field]: ossUrl
      };

      if (state.pendingAssetBlobs[field] === blobUrl) {
        delete state.pendingAssetBlobs[field];
      }
    },
    persist(state, action: PayloadAction<EditorialQuestion>) {
      state.persisted = action.payload;
    }
  }
});

export const draftQuestionActions = slice.actions;

export default slice.reducer;
